// @ts-nocheck
/**
 * listViewRenderer.ts — Template V2 scrollable list with separate selection index.
 */

import { formatListEntryForDisplay } from '../variables/listValueUtils';
import { componentInteractionProps } from './interactionProps';
import {
  isDocumentsItemFieldSource,
  projectDocumentListView,
} from './documentListViewProjection';
import { applyExpressionFallback, buildDescriptor, resolveValue } from './rendererUtils';
import type { CompiledPropsByType, ComponentRenderer } from './types';
import { extractTypedProps } from './types';

function coerceEntries(raw: unknown): unknown[] {
  if (Array.isArray(raw)) {
    return raw;
  }
  if (typeof raw === 'string' && raw.trim().length > 0) {
    try {
      const parsed = JSON.parse(raw) as unknown;
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
}

function coerceSelectionIndex(raw: unknown): number {
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    const n = Math.trunc(raw);
    // DEC-075: any negative index means nothing selected.
    return n < 0 ? -1 : n;
  }
  if (typeof raw === 'string' && raw.trim() !== '') {
    const n = Number(raw);
    if (Number.isFinite(n)) {
      const truncated = Math.trunc(n);
      return truncated < 0 ? -1 : truncated;
    }
  }
  return -1;
}

export const LIST_VIEW_RENDERER: ComponentRenderer<CompiledPropsByType['listView']> = {
  supportedType: 'listView',
  render(context) {
    const issuesBefore = context.issues.length;
    const { visible, enabled } = applyExpressionFallback(context);
    const p = extractTypedProps('listView', context.node.props);
    const selectedIndex = coerceSelectionIndex(resolveValue(p.listView.selectionSource, context));

    if (isDocumentsItemFieldSource(p.listView.source)) {
      const item = context.bindingContext?.item;
      const projected = item
        ? projectDocumentListView(item)
        : { entries: [] as string[], subtitles: [] as string[] };
      return buildDescriptor(context.node, visible, enabled, {
        entries: projected.entries,
        ...(projected.subtitles.length > 0 ? { subtitles: projected.subtitles } : {}),
        selectedIndex,
        editable: p.listView.editable ?? false,
        ...componentInteractionProps(context.node),
      }, { diagnostics: context.issues.slice(issuesBefore), renderContext: context });
    }

    const entries = coerceEntries(resolveValue(p.listView.source, context));
    const labels = entries.map((entry) => formatListEntryForDisplay(entry));

    return buildDescriptor(context.node, visible, enabled, {
      entries: labels,
      selectedIndex,
      editable: p.listView.editable ?? false,
      ...componentInteractionProps(context.node),
    }, { diagnostics: context.issues.slice(issuesBefore), renderContext: context });
  },
};
